import { request } from './client.js';
import { toQuery } from '../utils/params.js';

// A bulk action targets EITHER the rows the user ticked, or a whole filter
// ("select all 3,400 matching"). Filter mode sends the filter the table was
// built from plus the rows the user un-ticked — never thousands of ids — and
// the backend re-runs that filter to resolve the set.
//
// Accepts a bare id array too, so any caller that still passes one keeps
// working.
export function toSelection(selection) {
  if (Array.isArray(selection)) return { record_ids: selection };
  if (selection?.filter) {
    return {
      record_ids: [],
      filter: selection.filter,
      exclude_ids: selection.exclude_ids || [],
    };
  }
  return { record_ids: selection?.record_ids || [] };
}

export const billingApi = {
  list: (params = {}) => request(`/api/billing/records${toQuery(params)}`),
  facets: (params = {}) => request(`/api/billing/records/facets${toQuery(params)}`),
  get: (id) => request(`/api/billing/records/${id}`),
  // The SHAREABLE address of a record. `id` is IDENTITY and the reprocess
  // rebuilds the table, so an id link dies overnight; the key encodes the
  // natural key the whole system runs on. The token is opaque here on purpose
  // — the backend owns the codec, so there is no second copy to drift.
  getByKey: (key) => request(`/api/billing/records/by-key/${key}`),
  getLineItems: (id) => request(`/api/billing/records/${id}/line-items`),
  // What WOULD this action do to everything matching the filter? Counts only.
  // Backs the confirm dialog so the user commits to a real number instead of
  // a filter they can only see the first 100 rows of.
  bulkPreview: (action, filter, excludeIds = []) =>
    request('/api/billing/records/bulk-preview', {
      method: 'POST',
      body: JSON.stringify({ action, filter, exclude_ids: excludeIds }),
    }),
  updateStatus: (selection, status, notes = null) =>
    request('/api/billing/records/update-status', {
      method: 'POST',
      body: JSON.stringify({ ...toSelection(selection), status, notes }),
    }),
  // Terminal "billed outside the app" status + its explicit undo.
  markManuallyHandled: (selection, notes = null) =>
    request('/api/billing/records/update-status', {
      method: 'POST',
      body: JSON.stringify({
        ...toSelection(selection), status: 'manually_handled', notes,
      }),
    }),
  unmarkManuallyHandled: (selection) =>
    request('/api/billing/records/unmark-manually-handled', {
      method: 'POST',
      body: JSON.stringify(toSelection(selection)),
    }),
  // Terminal "ops says don't bill this" exclusion + its explicit undo.
  markExcluded: (selection, notes = null) =>
    request('/api/billing/records/mark-excluded', {
      method: 'POST',
      body: JSON.stringify({ ...toSelection(selection), notes }),
    }),
  unmarkExcluded: (selection) =>
    request('/api/billing/records/unmark-excluded', {
      method: 'POST',
      body: JSON.stringify(toSelection(selection)),
    }),
  acknowledge: (recordId, notes = null) =>
    request(`/api/billing/records/${recordId}/acknowledge`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    }),
  voidRecords: (selection) =>
    request('/api/billing/records/void', {
      method: 'POST',
      body: JSON.stringify(toSelection(selection)),
    }),
  retryRecords: (selection) =>
    request('/api/billing/records/retry', {
      method: 'POST',
      body: JSON.stringify(toSelection(selection)),
    }),
  repushRecords: (selection) =>
    request('/api/billing/records/repush', {
      method: 'POST',
      body: JSON.stringify(toSelection(selection)),
    }),
  summary: (batchId) =>
    request(`/api/billing/summary${batchId ? `?batch_id=${batchId}` : ''}`),
  audit: (limit = 100) => request(`/api/billing/audit?limit=${limit}`),
  alerts: () => request('/api/billing/alerts'),
};
